import { useState, useEffect } from 'react';
import { useWishlist } from '../../../hooks/useWishlist';
import { useNavigate } from 'react-router-dom';

export default function Hero() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [searchData, setSearchData] = useState({
    make: '',
    price: '',
    year: '',
    bodyType: ''
  });
  const { wishlistCount } = useWishlist();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = () => {
    // Build query parameters from search data
    const params = new URLSearchParams();
    
    if (searchData.make) params.append('make', searchData.make);
    if (searchData.price) params.append('price', searchData.price);
    if (searchData.year) params.append('year', searchData.year);
    if (searchData.bodyType) params.append('bodyType', searchData.bodyType);
    
    // Navigate to inventory page with search parameters
    const queryString = params.toString();
    navigate(`/inventory${queryString ? `?${queryString}` : ''}`);
  };

  const scrollToTradeIn = () => {
    navigate('/sell-trade');
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-b from-[#1E293B] via-[#334155] to-white overflow-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://static.readdy.ai/image/e5292f43a8fca29a2b9f75d3d42cfb52/a79cb0bec034472cb10b80478a2788a7.png" 
              alt="iCarga Logo" 
              className="h-16 w-16 rounded-full object-cover"
            />
            <span className={`text-3xl font-bold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              iCarGA
            </span>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a 
              onClick={() => navigate('/inventory')}
              className={`text-sm font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#2563EB]' : 'text-white hover:text-gray-200'}`}
            >
              Inventory
            </a>
            <a 
              onClick={() => navigate('/financing')}
              className={`text-sm font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#2563EB]' : 'text-white hover:text-gray-200'}`}
            >
              Financing
            </a>
            <a 
              onClick={() => navigate('/sell-trade')}
              className={`text-sm font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#2563EB]' : 'text-white hover:text-gray-200'}`}
            >
              Sell/Trade
            </a>
            <a 
              onClick={() => navigate('/reviews')}
              className={`text-sm font-light transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#2563EB]' : 'text-white hover:text-gray-200'}`}
            >
              Reviews
            </a>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/wishlist')}
              className={`relative w-10 h-10 flex items-center justify-center transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-red-500' : 'text-white hover:text-red-400'}`}
              title="View Wishlist"
            >
              <i className={`text-2xl ${wishlistCount > 0 ? 'ri-heart-fill text-red-500' : 'ri-heart-line'}`}></i>
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            <a 
              onClick={() => navigate('/financing')}
              className="px-6 py-2.5 bg-white text-[#1E293B] rounded-full text-sm font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Pre-Approved
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative pt-32 pb-6 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Find Your Perfect Ride
            </h1>
            <p className="text-lg text-gray-300 mb-8">
              No-hassle buying. Local pickup & delivery in Lithonia, GA
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <button 
                onClick={scrollToTradeIn}
                className="px-8 py-4 bg-[#10B981] text-white rounded-full text-base font-semibold hover:bg-[#059669] transition-all hover:scale-105 shadow-lg flex items-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-money-dollar-circle-line text-xl"></i>
                Get Trade-In Value
              </button>
              <button 
                onClick={() => navigate('/inventory')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-full text-base font-semibold hover:bg-white/20 transition-all border border-white/30 flex items-center gap-3 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-car-line text-xl"></i>
                Browse Inventory
              </button>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-2xl shadow-2xl p-2 flex flex-col md:flex-row items-stretch gap-2">
              <div className="flex-1 px-4 py-3 border-r border-gray-200">
                <label className="block text-xs text-gray-500 mb-1">Make & Model</label>
                <select 
                  value={searchData.make}
                  onChange={(e) => setSearchData({...searchData, make: e.target.value})}
                  className="w-full text-sm text-gray-900 bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="">Any Make</option>
                  <option value="Honda">Honda</option>
                  <option value="Toyota">Toyota</option>
                  <option value="Ford">Ford</option>
                  <option value="Chevrolet">Chevrolet</option>
                  <option value="Nissan">Nissan</option>
                  <option value="Mazda">Mazda</option>
                  <option value="Subaru">Subaru</option>
                  <option value="Hyundai">Hyundai</option>
                  <option value="Jeep">Jeep</option>
                  <option value="Kia">Kia</option>
                </select>
              </div>

              <div className="flex-1 px-4 py-3 border-r border-gray-200">
                <label className="block text-xs text-gray-500 mb-1">Price Range</label>
                <select 
                  value={searchData.price}
                  onChange={(e) => setSearchData({...searchData, price: e.target.value})}
                  className="w-full text-sm text-gray-900 bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="">Any Price</option>
                  <option value="0-15000">Under $15,000</option>
                  <option value="15000-25000">$15,000 - $25,000</option>
                  <option value="25000-30000">$25,000 - $30,000</option>
                  <option value="30000-50000">$30,000 - $50,000</option>
                </select>
              </div>

              <div className="flex-1 px-4 py-3 border-r border-gray-200">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <select 
                  value={searchData.year}
                  onChange={(e) => setSearchData({...searchData, year: e.target.value})}
                  className="w-full text-sm text-gray-900 bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="">Any Year</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                  <option value="2022">2022</option>
                  <option value="2021">2021</option>
                  <option value="2020">2020</option>
                  <option value="2019">2019</option>
                  <option value="2018">2018</option>
                  <option value="2017">2017</option>
                  <option value="2016">2016</option>
                </select>
              </div>

              <div className="flex-1 px-4 py-3 border-r border-gray-200">
                <label className="block text-xs text-gray-500 mb-1">Body Type</label>
                <select 
                  value={searchData.bodyType}
                  onChange={(e) => setSearchData({...searchData, bodyType: e.target.value})}
                  className="w-full text-sm text-gray-900 bg-transparent border-none outline-none cursor-pointer"
                >
                  <option value="">Any Type</option>
                  <option value="Sedan">Sedan</option>
                  <option value="SUV">SUV</option>
                  <option value="Truck">Truck</option>
                  <option value="Wagon">Wagon</option>
                </select>
              </div>

              <button 
                onClick={handleSearch}
                className="px-8 py-4 bg-[#2563EB] text-white rounded-xl font-medium hover:bg-[#1d4ed8] transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line text-lg"></i>
                Search Inventory
              </button>
            </div>
          </div>
        </div>

        {/* Floating Badge */}
        <div className="absolute bottom-10 right-10 bg-white/20 backdrop-blur-md px-6 py-3 rounded-full">
          <p className="text-white font-semibold text-sm">500+ Cars Available</p>
        </div>
      </div>
    </section>
  );
}
